import React from 'react';
import { motion } from 'framer-motion';
import { 
  Quote, 
  TrendingUp, 
  Clock, 
  ShieldCheck, 
  ArrowRight,
  Building2
} from 'lucide-react';

const Testimonials = () => {
  const easePremium = [0.22, 1, 0.36, 1];

  // Datos enriquecidos con métricas de negocio reales
  const featuredStory = {
    company: 'Constructora del Norte S.A.C.',
    logoInitials: 'CN',
    name: 'Carlos Ruiz',
    role: 'Jefe de Contabilidad',
    quote: "La automatización de la facturación electrónica y los reportes fiscales nos ahorró más de 30 horas semanales. Es una herramienta robusta que realmente entiende las necesidades tributarias locales.",
    metrics: [
      { icon: Clock, value: '30h', label: 'Ahorradas/semana' },
      { icon: ShieldCheck, value: '0', label: 'Multas por retraso' },
      { icon: TrendingUp, value: '100%', label: 'Conciliación auto.' }
    ]
  };

  const secondaryStories = [
    {
      company: 'Distribuidora Andina',
      name: 'Ana López',
      role: 'Gerente General',
      quote: 'Pasamos de hojas de cálculo desordenadas a un sistema en la nube que maneja todas nuestras sedes en tiempo real. El soporte fue impecable.',
      highlight: '100% Procesos digitalizados'
    },
    {
      company: 'Clínica San Rafael',
      name: 'María González',
      role: 'Directora de Operaciones',
      quote: 'La implementación del módulo de citas fue fluida. El equipo se adaptó a nuestros flujos de trabajo en lugar de obligarnos a cambiar los nuestros.',
      highlight: '+40% Eficiencia en citas'
    },
    {
      company: 'Logística Express',
      name: 'Juan Pérez',
      role: 'Fundador',
      quote: 'Empezamos con un módulo pequeño y, a medida que crecimos, el sistema escaló con nosotros. Hoy gestionamos toda nuestra flota con la misma plataforma.',
      highlight: 'Escalabilidad garantizada'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1, ease: easePremium }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: easePremium } }
  };

  return (
    <section id="testimonios" className="py-24 lg:py-32 bg-white relative overflow-hidden">
      
      {/* Textura de fondo ultra-sutil (Grid técnico) */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.02]"
        style={{
          backgroundImage: 'linear-gradient(#059669 1px, transparent 1px), linear-gradient(90deg, #059669 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        
        {/* ========================================== */}
        {/* 1. ENCABEZADO MINIMALISTA                 */}
        {/* ========================================== */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: easePremium }}
          className="max-w-3xl mb-20"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-4 uppercase tracking-wider">
            <Building2 className="w-4 h-4" />
            Casos de Éxito
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-[1.15]">
            Resultados que hablan <br className="hidden md:block" />
            por sí mismos.
          </h2>
        </motion.div>

        {/* ========================================== */}
        {/* 2. GRID ASIMÉTRICO (BENTO STYLE)          */}
        {/* ========================================== */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-24"
        >
          
          {/* TARJETA PRINCIPAL (Ocupa 7 columnas) */}
          <motion.div 
            variants={itemVariants}
            className="lg:col-span-7 bg-slate-50 rounded-2xl border border-slate-200 p-8 md:p-10 flex flex-col justify-between group hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-900/5 transition-all duration-500"
          >
            <div>
              <Quote className="w-8 h-8 text-emerald-600/40 mb-6" />
              <blockquote className="text-2xl md:text-3xl font-medium text-slate-900 leading-snug mb-8">
                "{featuredStory.quote}"
              </blockquote>
            </div>

            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center text-white font-bold text-sm">
                  {featuredStory.logoInitials}
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{featuredStory.name}</div>
                  <div className="text-sm text-slate-600">{featuredStory.role}, {featuredStory.company}</div>
                </div>
              </div>

              {/* Métricas de impacto */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-200">
                {featuredStory.metrics.map((metric, idx) => {
                  const Icon = metric.icon;
                  return (
                    <div key={idx}>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className="w-4 h-4 text-emerald-600" />
                        <span className="text-2xl font-bold text-slate-900 tracking-tight">{metric.value}</span>
                      </div>
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{metric.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* COLUMNAS DERECHAS (Ocupan 5 columnas, apiladas) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            {secondaryStories.map((story, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className="flex-1 bg-white rounded-2xl border border-slate-200 p-6 md:p-8 flex flex-col justify-center group hover:border-emerald-500/30 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {story.company}
                  </span>
                  <TrendingUp className="w-4 h-4 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                
                <blockquote className="text-slate-700 leading-relaxed mb-6 flex-grow">
                  "{story.quote}"
                </blockquote>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{story.name}</div>
                    <div className="text-xs text-slate-500">{story.role}</div>
                  </div>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
                    {story.highlight}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>





      </div>
    </section>
  );
};

export default Testimonials;